
import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CVEItem } from '@/types/cve';
import { useToast } from '../use-toast';
import { useLanguage } from '../useLanguage';

export function useCVESearch(
  loadYearData: (year: number) => Promise<void>,
  cveData: Record<number, CVEItem[]>,
  years: number[]
) {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('search') || '';

  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [isSearching, setIsSearching] = useState(!!initialQuery);
  const [searchResults, setSearchResults] = useState<CVEItem[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Handle global search across all years
  const handleGlobalSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setIsSearching(false);
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    setSearchLoading(true);

    const results: CVEItem[] = [];
    const yearsToSearch = [...years];
    const searchPromises = [];

    // Process years in batches to avoid memory issues
    for (let i = 0; i < yearsToSearch.length; i += 3) {
      const batchYears = yearsToSearch.slice(i, i + 3);

      const batchPromise = (async () => {
        for (const year of batchYears) {
          try {
            // Load year data if not already loaded
            if (!cveData[year] || cveData[year].length === 0) {
              await loadYearData(year);
            }

            // If data is available, search in it
            if (cveData[year] && cveData[year].length > 0) {
              const yearResults = cveData[year].filter(item =>
                item.cve_id.toLowerCase().includes(query.toLowerCase()) ||
                (language === 'en'
                  ? item.description.toLowerCase().includes(query.toLowerCase())
                  : item.description_pl.toLowerCase().includes(query.toLowerCase()))
              );

              results.push(...yearResults);
            }
          } catch (err) {
            console.error(`Error searching in year ${year}:`, err);
          }
        }
      })();

      searchPromises.push(batchPromise);
    }

    try {
      // Process batches concurrently but in a controlled manner
      for (let i = 0; i < searchPromises.length; i += 2) {
        const batchPromises = searchPromises.slice(i, i + 2);
        await Promise.all(batchPromises);
      }

      // Sort results by date (newest first)
      const sortedResults = results.sort((a, b) =>
        new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime()
      );

      setSearchResults(sortedResults);
    } catch (error) {
      console.error("Error in global search:", error);
      toast({
        title: 'Search Error',
        description: 'An error occurred while searching. Please try again.',
        variant: "destructive",
      });
    } finally {
      setSearchLoading(false);
    }
  }, [cveData, language, loadYearData, toast, years]);

  // Clear search query
  const clearSearch = () => {
    setSearchQuery('');
    setIsSearching(false);
    setSearchResults([]);
  };

  // Apply debounced search when query changes
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchQuery.trim()) {
        setSearchParams(prev => {
          prev.set('search', searchQuery);
          // Reset page when searching
          prev.set('page', '1');
          return prev;
        });
        handleGlobalSearch(searchQuery);
      } else {
        setSearchParams(prev => {
          prev.delete('search');
          return prev;
        });
        setIsSearching(false);
        setSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, handleGlobalSearch, setSearchParams]);

  return {
    searchQuery,
    isSearching,
    searchResults,
    searchLoading,
    setSearchQuery,
    clearSearch
  };
}
